function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useEffect, useMemo, useState } from 'react';
import { CustomModal, Button, TableList, PageHeader, Spinner, Typography, PageEmptyState, PageErrorState, FilterPickerGroup, Searchbox, MultiSelectFilterPicker } from 'seagull';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import styles from "./MapContactsModal.module.scss";
import { columnDefs, headerItems } from "./MapContactsModal.utils";
import { useStore, useStoreActions } from "../../../../store/store-context";
import MapContactsRow, { getContactMapStatus } from "./MapContactsRow";
import { modalSize } from "../../../../infra/modals";
import NoDataIcon from "../assets/NoDataIcon";
import { CreateProposalButton } from "../../../CreateProposalButton";
import { handleRequest } from "../../../../infra/utils";
import { contactModel } from "../../../../models";
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

function applyContactMappingStatusFilter(filter, contact) {
  return filter.length === 0 || filter.some(status => status === 'mapped' && contactModel.integratedId(contact) != null || status === 'notMapped' && contactModel.integratedId(contact) == null);
}

function applySearch(search, contact) {
  const normalizedSearch = search ? search.trim().toLowerCase() : search;
  const companyName = contactModel.companyName(contact).toLowerCase();
  const firstName = contactModel.firstName(contact).toLowerCase();
  const lastName = contactModel.lastName(contact).toLowerCase();
  const email = contactModel.email(contact).toLowerCase();
  return !normalizedSearch || "".concat(companyName, " ").concat(firstName, " ").concat(lastName, " ").concat(email).includes(normalizedSearch);
} // Adapter for react-window


function ContactsRowComponent(props) {
  const {
    data,
    index,
    style
  } = props;
  const integratedId = data.anchorContacts[index].integratedId;
  const integratedQboCustomer = integratedId ? data.qboCustomersDictionary[integratedId] : undefined;
  return /*#__PURE__*/React.createElement("div", {
    style: style
  }, /*#__PURE__*/React.createElement(MapContactsRow, {
    anchorContact: data.anchorContacts[index],
    qboCustomers: data.qboCustomers,
    integratedQboCustomer: integratedQboCustomer,
    changeContactLink: data.changeContactLink,
    createNewCustomer: data.createNewCustomer,
    testid: 'map-contacts-row',
    key: data.anchorContacts[index].id,
    onInputFocus: data.onInputFocus,
    onInputBlur: data.onInputBlur
  }));
}

export function MapContactsModal(props) {
  const {
    dismissModal,
    onChange,
    contactIds
  } = props;
  const {
    data: unsortedAnchorContacts,
    loading: loadingAnchorContacts,
    error: errorAnchorContacts
  } = useStore(state => state.contacts);
  const {
    data: qboCustomers,
    loading: loadingQboCustomers,
    error: errorQboCustomers
  } = useStore(state => state.qboCustomers);
  const {
    api,
    fetchContacts,
    fetchQboCustomers
  } = useStoreActions();
  const [anchorContacts, setAnchorContacts] = useState([]);
  const inputDebounceTime = useStore(state => state.appConfig.inputDebounceTime);
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  const [search, setSearch] = useState('');
  const [contactMappingStatusFilter, setContactMappingStatusFilter] = useState([]);
  const isLoading = loadingAnchorContacts || loadingQboCustomers;
  const modalState = useMemo(() => {
    const hasError = errorAnchorContacts || errorQboCustomers;
    const noAnchorContacts = unsortedAnchorContacts && unsortedAnchorContacts.length === 0;
    const hasData = unsortedAnchorContacts && qboCustomers;
    if (isLoading) return 'loading';else if (hasError) return 'error';else if (noAnchorContacts) return 'empty';else if (hasData) return 'valid';else {
      return 'loading';
    }
  }, [errorAnchorContacts, errorQboCustomers, isLoading, qboCustomers, unsortedAnchorContacts]);
  useEffect(() => {
    const filtered = contactIds !== null && contactIds !== void 0 && contactIds.length ? unsortedAnchorContacts === null || unsortedAnchorContacts === void 0 ? void 0 : unsortedAnchorContacts.filter(c => contactIds.includes(c.id) && !c.isSample) : unsortedAnchorContacts === null || unsortedAnchorContacts === void 0 ? void 0 : unsortedAnchorContacts.filter(c => !c.isSample);
    const sortProperties = ['firstName', 'lastName', 'companyName'];
    const orderedByName = orderBy(filtered, sortProperties, ['asc', 'asc', 'asc']);
    const sortedByIntegrationStatus = sortBy(orderedByName, c => !!c.integratedId);
    setAnchorContacts(sortedByIntegrationStatus);
  }, [contactIds, unsortedAnchorContacts]);
  const numOfMappedContacts = useMemo(() => {
    const mappedContacts = anchorContacts === null || anchorContacts === void 0 ? void 0 : anchorContacts.filter(c => !!c.integratedId);
    return (mappedContacts === null || mappedContacts === void 0 ? void 0 : mappedContacts.length) || 0;
  }, [anchorContacts]);
  const qboCustomersDictionary = useMemo(() => {
    return keyBy(qboCustomers, 'id');
  }, [qboCustomers]);

  async function refreshData() {
    fetchContacts();
    fetchQboCustomers();
  }

  async function changeContactLink(anchorContact, qboCustomerId) {
    const [, linkError, hasLinkError] = await handleRequest(api.contact.qboLink(anchorContact.id, {
      integratedId: qboCustomerId,
      version: anchorContact.version
    }));

    if (hasLinkError) {
      throw linkError;
    }

    onChange && onChange();
    const [updatedContact, updatedContactError, hasUpdatedContactErr] = await handleRequest(api.contact.getById(anchorContact.id));

    if (hasUpdatedContactErr) {
      throw updatedContactError;
    }

    const newAnchorContacts = anchorContacts.map(c => c.id === anchorContact.id ? updatedContact : c);
    setAnchorContacts(newAnchorContacts);
  }

  async function createNewCustomer(anchorContact) {
    await api.contact.qboCreate(anchorContact.id);
    onChange && onChange();
    const updatedContact = await api.contact.getById(anchorContact.id);
    const newAnchorContacts = anchorContacts.map(c => c.id == anchorContact.id ? updatedContact : c);
    setAnchorContacts(newAnchorContacts);
    await fetchQboCustomers();
  }

  const {
    filteredAnchorContacts,
    contactMappingStatusFilterOptions
  } = useMemo(() => {
    const searchFiltered = anchorContacts.filter(contact => applySearch(search, contact));
    const contactMappingStatusFilterOptions = searchFiltered.reduce((acc, contact) => {
      const status = getContactMapStatus(contact);
      const existingStatusEntry = acc.findIndex(option => option.value === status);

      if (existingStatusEntry >= 0) {
        acc[existingStatusEntry].counter = (acc[existingStatusEntry].counter || 0) + 1;
      } else {
        acc.push({
          value: status,
          counter: 1,
          label: status === 'mapped' ? 'Yes' : 'No'
        });
      }

      return acc;
    }, []);
    const filteredAnchorContacts = searchFiltered.filter(contact => applyContactMappingStatusFilter(contactMappingStatusFilter, contact));
    return {
      filteredAnchorContacts,
      contactMappingStatusFilterOptions
    };
  }, [search, contactMappingStatusFilter, anchorContacts]);
  useEffect(() => {
    refreshData();
  }, []);
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    "data-testid": "qbo-map-contacts-modal",
    footer: modalState === 'valid' && /*#__PURE__*/React.createElement("div", {
      className: styles.footer
    }, anchorContacts && /*#__PURE__*/React.createElement("div", {
      "data-testid": "total-map-status"
    }, /*#__PURE__*/React.createElement(Typography, null, "".concat(numOfMappedContacts, " of ").concat(anchorContacts.length, " contacts mapped"))), /*#__PURE__*/React.createElement(Button, {
      onClick: dismissModal,
      "data-testid": "qbo-map-contacts-close"
    }, "Close"))
  }, modalSize.large, {
    onClose: dismissModal
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.modalContainer
  }, /*#__PURE__*/React.createElement(PageHeader, {
    title: "Map contacts to QuickBooks Online",
    subheader: "Map Anchor contacts to QuickBooks Online customers."
  }), /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, modalState === 'loading' && /*#__PURE__*/React.createElement(LoadingState, null), modalState === 'empty' && /*#__PURE__*/React.createElement(EmptyState, null), modalState === 'error' && /*#__PURE__*/React.createElement(ErrorState, {
    onRefresh: refreshData
  }), modalState === 'valid' && /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    flexWrap: true,
    "data-testid": "map-contacts-modal-filters"
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: registerSearchBoxHotkey,
    value: search,
    "data-testid": "map-contacts-modal-search",
    debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
    onChange: event => setSearch(event.target.value),
    onClear: () => setSearch('')
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "qbo-mapping-status-filter-picker",
    value: contactMappingStatusFilter,
    options: contactMappingStatusFilterOptions,
    label: "Is mapped",
    onChange: contactMappingStatus => setContactMappingStatusFilter(contactMappingStatus)
  }))), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  }), filteredAnchorContacts && /*#__PURE__*/React.createElement("div", {
    className: styles.tableContainer
  }, /*#__PURE__*/React.createElement(AutoSizer, null, _ref => {
    let {
      height,
      width
    } = _ref;
    return /*#__PURE__*/React.createElement(FixedSizeList, {
      itemData: {
        anchorContacts: filteredAnchorContacts,
        qboCustomers,
        qboCustomersDictionary,
        createNewCustomer,
        changeContactLink,
        onInputFocus: () => setRegisterSearchBoxHotkey(false),
        onInputBlur: () => setRegisterSearchBoxHotkey(true)
      },
      itemCount: filteredAnchorContacts.length,
      itemSize: 64,
      height: height,
      width: width,
      overscanCount: 50
    }, ContactsRowComponent);
  }))))));
}

const LoadingState = () => /*#__PURE__*/React.createElement("div", {
  className: styles.loading
}, /*#__PURE__*/React.createElement(Spinner, {
  size: 40,
  "data-testid": "map-contacts-loading"
}));

const EmptyState = () => /*#__PURE__*/React.createElement(PageEmptyState, {
  "data-testid": "map-contacts-empty-state",
  illustration: /*#__PURE__*/React.createElement(NoDataIcon, null),
  message: "In order to map with your QuickBooks online account, start by creating an agreement",
  actions: /*#__PURE__*/React.createElement(CreateProposalButton, {
    text: "Create a new agreement",
    hidePrefix: true,
    "data-testid": "map-contacts-new-btn"
  })
});

const ErrorState = _ref2 => {
  let {
    onRefresh
  } = _ref2;
  return /*#__PURE__*/React.createElement(PageErrorState, {
    "data-testid": "map-contacts-error-state",
    message: "Failed to retrieve the relevant data please try again later",
    onRefresh: onRefresh,
    skin: "secondary"
  });
};